import NotificationsCenter from '../dashboards/NotificationsCenter';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNotifications } from '../../hooks/useNotifications';
import { usePendingGiftsMenuBadge } from '../../hooks/usePendingGiftsMenuBadge';
import DashboardLayout from '../ui/DashboardLayout';
import BackToTopButton from '../ui/BackToTopButton';
import { FullPageSkeleton } from '../ui/PageSkeleton';
import Modal from '../ui/Modal';
import UserAvatar from '../ui/UserAvatar';
import CopyablePhone from '../ui/CopyablePhone';
import RewardsConfig from '../dashboards/RewardsConfig';
import VendorProspectsView from '../enrola/VendorProspectsView';
import PendingGiftsView from '../enrola/PendingGiftsView';
import AdminVendorReviewsView from '../enrola/AdminVendorReviewsView';
import VideoHubAdmin from '../videos/VideoHubAdmin';
import PlatformHomeView from './PlatformHomeView';
import PlatformCompaniesView from './PlatformCompaniesView';
import PlatformSettingsPanel from './PlatformSettingsPanel';
import PlatformContextBar from './PlatformContextBar';
import PlatformUsersView from './PlatformUsersView';
import PlatformCompanyDetailView from './PlatformCompanyDetailView';
import PlatformAnalyticsView from './PlatformAnalyticsView';
import PlatformBackupsView from './PlatformBackupsView';
import { navigateFromNotificationAction } from '../../utils/notifications';

const CONTEXT_VIEWS = new Set([
  'users',
  'clients',
  'analytics',
  'prospects',
  'rewards',
  'pendingGifts',
  'vendorReviews',
  'videoHub',
]);

function pathToView(pathname) {
  const seg = pathname.split('/').filter(Boolean);
  const second = seg[1] || '';
  const third = seg[2] || '';
  if (second === 'companies' && third) return 'companyDetail';
  const map = {
    companies: 'companies',
    users: 'users',
    clients: 'clients',
    analytics: 'analytics',
    settings: 'settings',
    prospects: 'prospects',
    rewards: 'rewards',
    'pending-gifts': 'pendingGifts',
    'vendor-reviews': 'vendorReviews',
    'video-hub': 'videoHub',
    backups: 'backups',
  };
  return map[second] || 'dashboard';
}

function viewToPath(view, companyId = null) {
  if (view === 'companyDetail' && companyId) {
    return `/admin/companies/${companyId}`;
  }
  const map = {
    companies: '/admin/companies',
    users: '/admin/users',
    clients: '/admin/clients',
    analytics: '/admin/analytics',
    settings: '/admin/settings',
    prospects: '/admin/prospects',
    rewards: '/admin/rewards',
    pendingGifts: '/admin/pending-gifts',
    vendorReviews: '/admin/vendor-reviews',
    videoHub: '/admin/video-hub',
    backups: '/admin/backups',
  };
  return map[view] || '/admin';
}

function PlatformAdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const adminT = t.admin;
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [detailCompanyId, setDetailCompanyId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [settingsTab, setSettingsTab] = useState('account');
  const [settingsCompanyId, setSettingsCompanyId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('all');

  const { count: pendingGiftsBadgeCount, refreshCount: refreshPendingGiftsBadge } = usePendingGiftsMenuBadge(
    'admin',
    30000,
    selectedCompanyId,
  );

  const companies = overview?.companies || [];

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(false);
    try {
      const response = await axios.get('/api/admin/platform/overview', { withCredentials: true });
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to load platform overview', error);
      setOverviewError(true);
      toast.error(pa.failedToLoadOverview);
    } finally {
      setOverviewLoading(false);
    }
  }, [pa.failedToLoadOverview]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/users', { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error(adminT.errorLoadingUsers);
    } finally {
      setLoading(false);
    }
  }, [adminT.errorLoadingUsers]);

  useEffect(() => {
    fetchOverview();
    fetchUsers();
  }, [fetchOverview, fetchUsers]);

  useEffect(() => {
    const viewFromPath = pathToView(location.pathname);
    const seg = location.pathname.split('/').filter(Boolean);
    if (location.pathname.endsWith('/notifications')) {
      setShowNotificationsModal(true);
      navigate('/admin', { replace: true });
      return;
    }
    if (viewFromPath === 'companyDetail') {
      const companyId = Number(seg[2]);
      const validId = Number.isFinite(companyId) ? companyId : null;
      setDetailCompanyId(validId);
      if (validId) {
        setSelectedCompanyId(validId);
        setCompanyFilter(String(validId));
        setSettingsCompanyId(validId);
      }
      setActiveView('companyDetail');
      return;
    }
    setDetailCompanyId(null);
    setActiveView(viewFromPath);
  }, [location.pathname, navigate]);

  const syncCompanyContext = (companyId) => {
    setSelectedCompanyId(companyId);
    setCompanyFilter(companyId ? String(companyId) : 'all');
    setSettingsCompanyId(companyId);
  };

  const navigateToView = (view, options = {}) => {
    if (view === 'settings') {
      if (options.tab) setSettingsTab(options.tab);
      const settingsCompany = options.companyId ?? selectedCompanyId;
      if (settingsCompany) {
        setSettingsCompanyId(settingsCompany);
        syncCompanyContext(settingsCompany);
      }
    } else if (options.companyId) {
      syncCompanyContext(options.companyId);
    }
    if (view === 'companyDetail' && options.companyId) {
      navigate(viewToPath('companyDetail', options.companyId));
      return;
    }
    navigate(viewToPath(view));
  };

  const configureCompany = (companyId) => {
    navigateToView('settings', { tab: 'rewards', companyId });
  };

  const handleNotificationNavigate = useCallback((actionPath) => {
    navigateFromNotificationAction(navigate, actionPath);
  }, [navigate]);

  const getPageTitle = () => {
    if (activeView === 'companyDetail') {
      const company = companies.find((entry) => entry.id === detailCompanyId);
      return company?.name || pa.companyDetailTitle;
    }
    const titles = {
      companies: pa.companiesTitle,
      users: pa.usersTitle,
      clients: pa.clientsTitle,
      analytics: pa.analyticsTitle,
      settings: pa.settingsTitle,
      prospects: pa.prospectsTitle,
      rewards: pa.rewardsTitle,
      pendingGifts: pa.pendingGiftsTitle,
      vendorReviews: pa.vendorReviewsTitle,
      videoHub: pa.videoHubTitle,
      backups: pa.backupsTitle,
    };
    return titles[activeView] || pa.dashboardTitle;
  };

  const getPageSubtitle = () => {
    if (activeView === 'companyDetail') return pa.companyDetailSubtitle;
    const subtitles = {
      companies: pa.companiesSubtitle,
      users: pa.usersSubtitle,
      clients: pa.clientsSubtitle,
      analytics: pa.analyticsSubtitle,
      settings: pa.settingsSubtitle,
      prospects: pa.prospectsSubtitle,
      rewards: pa.rewardsSubtitle,
      pendingGifts: pa.pendingGiftsSubtitle,
      vendorReviews: pa.vendorReviewsSubtitle,
      videoHub: pa.videoHubSubtitle,
      backups: pa.backupsSubtitle,
    };
    return subtitles[activeView] || pa.dashboardSubtitle;
  };

  const renderContextBar = () => {
    if (!CONTEXT_VIEWS.has(activeView)) return null;
    return (
      <PlatformContextBar
        companies={companies}
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={syncCompanyContext}
        onViewCompany={(companyId) => navigateToView('companyDetail', { companyId })}
        onConfigureCompany={configureCompany}
        onViewUsers={(companyId) => navigateToView('users', { companyId })}
      />
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'companyDetail':
        return (
          <PlatformCompanyDetailView
            companyId={detailCompanyId}
            onBack={() => navigateToView('companies')}
            onConfigure={configureCompany}
            onViewUsers={(companyId) => navigateToView('users', { companyId })}
            onViewProspects={(companyId) => navigateToView('prospects', { companyId })}
          />
        );
      case 'companies':
        return (
          <PlatformCompaniesView
            companies={companies}
            loading={overviewLoading}
            loadError={overviewError}
            onRetry={fetchOverview}
            onConfigureCompany={configureCompany}
            onViewCompany={(companyId) => navigateToView('companyDetail', { companyId })}
          />
        );
      case 'users':
        return (
          <PlatformUsersView
            users={users}
            companies={companies}
            mode="all"
            companyFilter={companyFilter}
            onCompanyFilterChange={(value) => {
              setCompanyFilter(value);
              setSelectedCompanyId(value === 'all' ? null : Number(value));
            }}
            onViewUser={(entry) => {
              setSelectedUser(entry);
              setShowUserModal(true);
            }}
          />
        );
      case 'clients':
        return (
          <PlatformUsersView
            users={users}
            companies={companies}
            mode="clients"
            companyFilter={companyFilter}
            onCompanyFilterChange={(value) => {
              setCompanyFilter(value);
              setSelectedCompanyId(value === 'all' ? null : Number(value));
            }}
            onViewUser={(entry) => {
              setSelectedUser(entry);
              setShowUserModal(true);
            }}
          />
        );
      case 'analytics':
        return (
          <PlatformAnalyticsView
            companies={companies}
            overview={overview}
            selectedCompanyId={selectedCompanyId}
            onSelectCompany={(companyId) => navigateToView('companyDetail', { companyId })}
          />
        );
      case 'settings':
        return (
          <PlatformSettingsPanel
            companies={companies}
            companiesLoading={overviewLoading}
            initialTab={settingsTab}
            initialCompanyId={settingsCompanyId || selectedCompanyId}
            onCompanyConfigured={syncCompanyContext}
            onNavigateView={navigateToView}
          />
        );
      case 'rewards':
        return <RewardsConfig key={selectedCompanyId || 'none'} presetCompanyId={selectedCompanyId} />;
      case 'prospects':
        return (
          <VendorProspectsView
            mode="admin"
            companyFilter={selectedCompanyId}
            requestCompanyId={selectedCompanyId}
          />
        );
      case 'pendingGifts':
        return (
          <PendingGiftsView
            role="admin"
            companyFilter={selectedCompanyId}
            requestCompanyId={selectedCompanyId}
            onPendingCountChange={refreshPendingGiftsBadge}
          />
        );
      case 'vendorReviews':
        return (
          <AdminVendorReviewsView
            companyFilter={selectedCompanyId}
            requestCompanyId={selectedCompanyId}
          />
        );
      case 'videoHub':
        return <VideoHubAdmin key={selectedCompanyId || 'none'} presetCompanyId={selectedCompanyId} />;
      case 'backups':
        return <PlatformBackupsView />;
      default:
        return (
          <PlatformHomeView
            overview={overview}
            loading={overviewLoading}
            loadError={overviewError}
            onRetry={fetchOverview}
            username={user?.username}
            onOpenCompanies={() => navigateToView('companies')}
            onOpenUsers={() => navigateToView('users')}
            onOpenAnalytics={() => navigateToView('analytics')}
            onOpenSettings={() => navigateToView('settings')}
            onOpenPendingGifts={(companyId) => navigateToView('pendingGifts', { companyId })}
            onOpenProspects={() => navigateToView('prospects')}
            onSelectCompany={(companyId) => navigateToView('companyDetail', { companyId })}
          />
        );
    }
  };

  if (loading) {
    return <FullPageSkeleton />;
  }

  return (
    <DashboardLayout
      activeView={activeView === 'companyDetail' ? 'companies' : activeView}
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      sidebarProps={{
        activeView: activeView === 'companyDetail' ? 'companies' : activeView,
        setActiveView: navigateToView,
        onLogout: logout,
        notificationsCount: unreadCount,
        menuBadges: { pendingGifts: pendingGiftsBadgeCount },
        userType: 'platformAdmin',
      }}
      topBarProps={{
        title: getPageTitle(),
        subtitle: getPageSubtitle(),
        onNotificationClick: () => setShowNotificationsModal(true),
        notificationsCount: unreadCount,
        onSettingsClick: () => navigateToView('settings', { companyId: selectedCompanyId }),
      }}
      overlays={
        <>
          <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)} title={adminT.userDetails} size="large">
            {selectedUser ? (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <UserAvatar src={selectedUser.avatar_url} alt={selectedUser.username} size="lg" variant="brand" />
                  <div>
                    <h3 className="text-xl font-bold text-secondary-900 ">{selectedUser.username}</h3>
                    <p className="text-secondary-600 ">{selectedUser.email}</p>
                    <p className="text-sm text-secondary-500 ">{selectedUser.company_name || pa.platformLevel}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.type}</p>
                    <p className="mt-1 text-sm text-secondary-900 ">
                      {selectedUser.is_admin ? adminT.administrators : selectedUser.is_vendor ? adminT.vendors : adminT.clients}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.memberSince}</p>
                    <p className="mt-1 text-sm text-secondary-900 ">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.contact}</p>
                    <CopyablePhone value={selectedUser.phone_numbers} fallback={adminT.noPhone} className="mt-1 text-sm" />
                  </div>
                  {selectedUser.is_client ? (
                    <>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.clientState}</p>
                        <p className="mt-1 text-sm capitalize text-secondary-900 ">{selectedUser.state || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.rewardPoints}</p>
                        <p className="mt-1 text-sm text-secondary-900 ">{selectedUser.reward_points ?? 0}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.totalReferrals}</p>
                        <p className="mt-1 text-sm text-secondary-900 ">{selectedUser.referrals_count ?? 0}</p>
                      </div>
                    </>
                  ) : null}
                  {selectedUser.is_vendor ? (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.clientsShort}</p>
                      <p className="mt-1 text-sm text-secondary-900 ">{selectedUser.clients_count ?? 0}</p>
                    </div>
                  ) : null}
                </div>

                {selectedUser.company_id ? (
                  <div className="flex flex-wrap gap-2 border-t border-secondary-200 pt-4 ">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserModal(false);
                        navigateToView('companyDetail', { companyId: selectedUser.company_id });
                      }}
                      className="rounded-lg border border-secondary-200 px-3 py-2 text-sm font-medium text-secondary-700  "
                    >
                      {pa.viewCompanyDetail}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserModal(false);
                        configureCompany(selectedUser.company_id);
                      }}
                      className="rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white"
                    >
                      {pa.configureCompany}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Modal>

          <NotificationsCenter
            isOpen={showNotificationsModal}
            onClose={() => setShowNotificationsModal(false)}
            onNavigate={handleNotificationNavigate}
          />
        </>
      }
    >
      <div className="space-y-6">
        {renderContextBar()}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          {renderContent()}
        </motion.div>
      </div>
      <BackToTopButton />
    </DashboardLayout>
  );
}

export default PlatformAdminDashboard;
